import { useEffect, useState, useCallback } from "react";
import { Link } from "react-router-dom";
import { Check, X, RefreshCw, ExternalLink, Play, Eye } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { PageHeader, Table, Td, StatusBadge, money } from "../../components/dashboard/Shared";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../../components/ui/dialog";

export default function Payments() {
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(false);
  const [detail, setDetail] = useState(null);
  const [action, setAction] = useState(null); // { invoice, action }
  const [notes, setNotes] = useState("");

  const load = useCallback(async () => {
    setBusy(true);
    try { const { data } = await api.get("/admin/payments/pending"); setData(data); }
    catch { setData({ count: 0, invoices: [] }); }
    finally { setBusy(false); }
  }, []);
  useEffect(() => { load(); const t = setInterval(load, 20000); return () => clearInterval(t); }, [load]);

  const openDetail = async (id) => {
    setDetail("loading");
    try { const { data } = await api.get(`/admin/payments/${id}`); setDetail(data); }
    catch { toast.error("Failed to load"); setDetail(null); }
  };

  const perform = async () => {
    if (!action) return;
    try {
      await api.post(`/admin/payments/${action.invoice.invoice_id}/${action.action}`, { notes });
      toast.success(`Payment ${action.action}d`);
      setAction(null); setNotes(""); load();
    } catch (err) { toast.error(err.response?.data?.detail || "Action failed"); }
  };

  const rows = data?.invoices || [];
  const totalPending = rows.reduce((s, i) => s + (i.amount_due || i.fee_amount || 0), 0);
  const awaiting = rows.filter((i) => i.status === "awaiting_confirmation").length;

  return (
    <div>
      <PageHeader title="Payments & Revenue" sub={`${data?.count || 0} outstanding · ${awaiting} awaiting confirmation · ${money(totalPending)} pending`}>
        <button onClick={load} disabled={busy} data-testid="payments-refresh" className="btn-outline px-3 py-1.5 text-xs flex items-center gap-2 disabled:opacity-50">
          <RefreshCw size={12} className={busy ? "animate-spin" : ""} /> Refresh
        </button>
      </PageHeader>

      {!data ? <div className="skeleton h-64" /> : rows.length === 0 ? (
        <div className="card-dark p-12 text-center" data-testid="no-pending-payments">
          <p className="text-gray-300">No pending payments.</p>
        </div>
      ) : (
        <Table headers={["Invoice", "User", "Period", "Fee", "Amount Paid", "Status", "Submitted", "Actions"]} testId="payments-table">
          {rows.map((i) => (
            <tr key={i.invoice_id} className="border-b border-white/5" data-testid={`payment-row-${i.invoice_id}`}>
              <Td className="text-xs tabular text-white">{i.invoice_id}</Td>
              <Td>
                <Link to={`/admin/users/${i.user_id}`} className="text-white hover:text-[#00F0FF] inline-flex items-center gap-1">{i.user?.name || i.user?.email} <ExternalLink size={10} className="opacity-50" /></Link>
                <p className="text-xs text-gray-500">{i.user?.email}</p>
              </Td>
              <Td className="tabular text-xs">{i.period}</Td>
              <Td className="tabular text-[#FFD600]">{money(i.fee_amount)}</Td>
              <Td className="tabular text-white">{i.payment_amount_paid ? money(i.payment_amount_paid) : "—"}</Td>
              <Td><StatusBadge status={i.status} /></Td>
              <Td className="text-xs tabular text-gray-500">{i.payment_submitted_at ? String(i.payment_submitted_at).slice(0, 16).replace("T", " ") : "—"}</Td>
              <Td>
                <div className="flex gap-2">
                  <button data-testid={`view-payment-${i.invoice_id}`} onClick={() => openDetail(i.invoice_id)} className="text-xs px-2.5 py-1 rounded btn-outline flex items-center gap-1"><Eye size={11} /> View</button>
                  {i.status === "awaiting_confirmation" && (
                    <>
                      <button data-testid={`approve-payment-${i.invoice_id}`} onClick={() => { setAction({ invoice: i, action: "approve" }); setNotes(""); }} className="text-xs px-2.5 py-1 rounded bg-[#00E676]/10 border border-[#00E676]/30 text-[#00E676] hover:bg-[#00E676]/15 flex items-center gap-1"><Check size={11} /> Approve</button>
                      <button data-testid={`reject-payment-${i.invoice_id}`} onClick={() => { setAction({ invoice: i, action: "reject" }); setNotes(""); }} className="text-xs px-2.5 py-1 rounded bg-[#FF3B30]/10 border border-[#FF3B30]/30 text-[#FF3B30] hover:bg-[#FF3B30]/15 flex items-center gap-1"><X size={11} /> Reject</button>
                    </>
                  )}
                </div>
              </Td>
            </tr>
          ))}
        </Table>
      )}

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-gray-200 max-w-2xl">
          <DialogHeader><DialogTitle className="font-display text-white">Payment Details</DialogTitle></DialogHeader>
          {detail === "loading" ? <div className="skeleton h-48" /> : detail && (
            <div className="space-y-4 text-sm">
              <div className="grid grid-cols-2 gap-3">
                <div><p className="label-xs">Invoice</p><p className="tabular text-white">{detail.invoice.invoice_id}</p></div>
                <div><p className="label-xs">Period</p><p className="tabular text-white">{detail.invoice.period}</p></div>
                <div><p className="label-xs">Weekly Profit</p><p className="tabular text-[#00E676]">{money(detail.invoice.weekly_profit)}</p></div>
                <div><p className="label-xs">Fee (50%)</p><p className="tabular text-[#FFD600]">{money(detail.invoice.fee_amount)}</p></div>
                <div><p className="label-xs">Amount Paid</p><p className="tabular text-white">{detail.invoice.payment_amount_paid ? money(detail.invoice.payment_amount_paid) : "—"}</p></div>
                <div><p className="label-xs">TX Hash</p><p className="tabular text-xs text-white break-all">{detail.invoice.payment_tx_hash || "—"}</p></div>
              </div>
              {detail.invoice.payment_screenshot_b64 && (
                <div>
                  <p className="label-xs mb-2">Payment Screenshot</p>
                  <img src={detail.invoice.payment_screenshot_b64.startsWith("data:") ? detail.invoice.payment_screenshot_b64 : `data:image/png;base64,${detail.invoice.payment_screenshot_b64}`} alt="Payment proof" className="max-h-96 w-auto rounded border border-white/10" data-testid="payment-screenshot" />
                </div>
              )}
              {detail.invoice.payer_note && (
                <div><p className="label-xs">Payer note</p><p className="text-white text-xs">{detail.invoice.payer_note}</p></div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      <Dialog open={!!action} onOpenChange={(o) => !o && setAction(null)}>
        <DialogContent className="bg-[#121212] border border-white/10 text-gray-200 max-w-md">
          <DialogHeader><DialogTitle className="font-display text-white capitalize">{action?.action} Payment</DialogTitle></DialogHeader>
          {action && (
            <div className="space-y-4">
              <p className="text-sm text-gray-400">Invoice <span className="text-white">{action.invoice.invoice_id}</span> · <span className="text-[#FFD600]">{money(action.invoice.fee_amount)}</span></p>
              <div>
                <label className="label-xs block mb-1">{action.action === "approve" ? "Confirmation note (optional)" : "Reason"}</label>
                <textarea data-testid="payment-action-notes" className="input-dark min-h-[80px]" maxLength={500} value={notes} onChange={(e) => setNotes(e.target.value)} />
              </div>
              <div className="flex gap-3">
                <button onClick={() => setAction(null)} data-testid="payment-action-cancel" className="flex-1 btn-outline py-2.5 text-sm">Cancel</button>
                <button onClick={perform} data-testid="payment-action-confirm" className={`flex-1 py-2.5 text-sm font-medium rounded capitalize ${action.action === "approve" ? "bg-[#00E676] text-black" : "bg-[#FF3B30] text-white"}`}>
                  {action.action === "approve" ? <><Play size={12} className="inline mr-1" /> Confirm & Resume Trading</> : `Confirm reject`}
                </button>
              </div>
            </div>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
